import React, { useEffect, useRef, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import cn from "classnames";
import { GridStack, GridItemHTMLElement, GridStackNode } from "gridstack";
import "gridstack/dist/gridstack.min.css";
import styles from "./dashboard-tile-grid.module.scss";
import { Dashboard, WidgetPositioning } from "@src/types/dashboards";
import { useDispatch, useSelector } from "react-redux";
import {
  updateModalsCloseDrawerModal,
  updateEditWidgetDrawerModalTileId,
} from "@src/reducers/your-reducer";
import {
  updateWidgetPositioning,
  upsertDashboard,
  removePendingWidget,
  addServerCommittedWidget,
} from "@src/reducers/dashboards-slice";
import { useI18N } from "@src/language/language-context";
import { RootState } from "@src/reducers/store";
import { WidgetRenderer } from "@src/components/organisms/widgets/widget-renderer";
import { useCheckWidgetInViewport } from "@src/hooks/use-check-widget-in-viewport";
import { getWidgetSize } from "@src/components/organisms/widgets/widget-sizes";
import { findFreeSlot } from "@src/components/organisms/widgets/find-free-slot";
import { patchPositioningV2UseCase } from "@src/cloud-connect/dashboards-v2/use-cases/patch-positioning-v2-use-case";
import { deleteWidgetV2UseCase } from "@src/cloud-connect/dashboards-v2/use-cases/delete-widget-v2-use-case";
import { getDashboardByIdV2UseCase } from "@src/cloud-connect/dashboards-v2/use-cases/get-dashboard-by-id-v2-use-case";
import { postDuplicateWidgetV2UseCase } from "@src/cloud-connect/dashboards-v2/use-cases/post-duplicate-widget-v2-use-case";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import ellipsisIcon from "@src/assets/svg/ellipsis-horizontal-circle.svg";

interface DashboardTileGridProps {
  dashboard: Dashboard;
  /** Read-only mode for the public viewer page at `/dashboard`.
   *  When `true`:
   *    - Gridstack is initialised with `staticGrid: true` so tiles
   *      cannot be dragged or resized.
   *    - The per-tile ellipsis menu is not rendered (no edit /
   *      duplicate / delete controls reachable).
   *    - The Gridstack change handler is a no-op, so even a stale
   *      grid instance cannot dispatch `updateWidgetPositioning`
   *      or PATCH positions to the BE.
   *  Default `false` so the existing editor page is unaffected.
   *  Skipped: lazy-load PATCH and grid-destroy cleanup stay
   *  unchanged — those are unmount paths, not user actions. */
  readOnly?: boolean;
}

const cssClassName = "dashboard-tile-grid";
const DEFAULT_GRID_COLUMNS = 24;
const CELL_HEIGHT = 30;

/** Observes the tile's content box; reports its visibility to the widget's
 *  fetch gate so off-screen widgets neither fetch nor poll.
 *  The IO root is the dashboard's scroll container, passed via a stable
 *  ref from `DashboardTileGrid`. The ref is read inside the IO hook's
 *  useEffect (post-commit, post-ref-attach), so the FIRST observer is
 *  built against the real container element — no race window with the
 *  document viewport where an off-screen tile could briefly register as
 *  intersecting and trip the fetch gate. */
const TrackVisibility = ({
  scrollContainerRef,
  children,
}: {
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
  children: (inView: boolean) => React.ReactNode;
}) => {
  const { ref, inView } =
    useCheckWidgetInViewport<HTMLDivElement>(scrollContainerRef);
  return (
    <div ref={ref} className={styles[`${cssClassName}__tile-viewport`]}>
      {children(inView)}
    </div>
  );
};

export const DashboardTileGrid = ({
  dashboard,
  readOnly = false,
}: DashboardTileGridProps) => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const { widgets, positioning } = dashboard;
  // Pending widgets (added-from-picker / local duplicates) are not committed
  // to the BE yet — the Drawer's handleClose removes them on Cancel, and the
  // Duplicate menu option is gated off them to avoid cloning an unsaved
  // draft (would-be a confusing "duplicate of an unsaved widget" state).
  const pendingWidgetIds = useSelector(
    (state: RootState) => state.dashboards.pendingWidgetIds,
  );
  const [openMenuWidgetId, setOpenMenuWidgetId] = useState<string | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<{
    rect: DOMRect;
    widgetId: string;
  } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // buttonRefsRef: each tile menu button gets its element captured here so
  // the portal popover can position itself against the viewport (avoids the
  // tile-inner's `overflow: hidden` clip and the canvas `overflow-y: auto`
  // scrollbar when the popover is taller than the tile).
  const buttonRefsRef = useRef<Map<string, HTMLButtonElement>>(new Map());

  const gridRef = useRef<HTMLDivElement>(null);
  const tileGridContainerRef = useRef<HTMLDivElement>(null);
  const gridInstanceRef = useRef<GridStack | null>(null);
  const isUpdatingFromRedux = useRef(false);
  // Tracks the widget ids already known to Gridstack so the sync effect can
  // add/remove incrementally instead of triggering a destroy+init on every
  // widget change. destroy+init silently drops the min/max constraints of
  // already-managed tiles because Gridstack doesn't re-read `gs-min-w/h/max-
  // w/h` attrs from existing DOM nodes after re-init.
  // Each id → its currently-mounted DOM element, AFTER Gridstack has been
  // notified via makeWidget. We can't use a plain id list + querySelector at
  // remove time because React unmounts the div BEFORE the effect fires, so
  // the look-up returns null and removeWidget is silently skipped — leaving
  // a ghost node in grid.engine. The map entries survive React unmounts
  // because we hold the element ref ourselves.
  const managedTilesRef = useRef<Map<string, HTMLElement>>(new Map());

  const selectedDashboardId = useSelector(
    (state: RootState) => state.dashboards.selectedDashboardId,
  );

  const texts = {
    editWidget: i18n("modules.dashboardTileGrid.editWidget"),
    duplicateWidget: i18n("modules.dashboardTileGrid.duplicateWidget"),
    deleteWidget: i18n("modules.dashboardTileGrid.deleteWidget"),
  };

  // "Latest ref" pattern — GridStack registers its change listener once on mount,
  // so a regular callback would capture stale state (positioning, selectedDashboardId).
  // We store the handler in a stable ref and replace .current on every render with a
  // fresh callback that sees the latest values. The GridStack listener always calls
  // through the ref, so it always reaches the most recent handler.
  // IMPORTANT: Needed to avoid re-subscribing to GridStack events on every render,
  // which causes performance issues and visual flicker.
  const handleGridChangeRef = useRef<(items: GridStackNode[]) => void>(
    () => ({}),
  );
  // Trailing debounce for PATCH persistence of positions after drag/resize.
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Only one PATCH is sent at a time,
  // and while it runs new drags wait their turn in `persistPositioningRef`.
  const persistInFlightRef = useRef<Promise<void> | null>(null);
  const persistPositioningRef = useRef<{
    dashboardId: string;
    positioning: Record<string, WidgetPositioning>;
    // Used to put the tiles back when the PATCH is rejected — the reducer already
    // holds the new positions, so without this the FE would keep showing a
    // layout the BE refused.
    previous: Record<string, WidgetPositioning>;
  } | null>(null);
  // The layout currently in the reducer, kept together with its dashboard id so
  // a revert can never merge one dashboard's tiles into another.
  const liveLayoutRef = useRef({ dashboardId: dashboard.id, positioning });
  liveLayoutRef.current = { dashboardId: dashboard.id, positioning };

  const flushPositioning = useCallback((): Promise<void> => {
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
      persistTimerRef.current = null;
    }
    // Wait for the PATCH that is already running. It sends the waiting layout
    // when it comes back, and the delete flow needs to run after every save.
    if (persistInFlightRef.current) return persistInFlightRef.current;
    const pending = persistPositioningRef.current;
    if (!pending) return Promise.resolve();
    persistPositioningRef.current = null;
    const send = patchPositioningV2UseCase(
      pending.dashboardId,
      pending.positioning,
    )
      .then((result) => {
        persistInFlightRef.current = null;
        if (!result.success) {
          console.error("Failed to persist widget positions:", result.error);
          errorSnackbar(result.error ?? "Failed to save widget positions");
          const newer = persistPositioningRef.current;
          if (newer && newer.dashboardId === pending.dashboardId) {
            // A drag is waiting and has not been sent, so give it this layout
            // as its fallback. Putting the tiles back now would yank what the
            // user is dragging.
            newer.previous = pending.previous;
          } else {
            // Merge into the current layout instead of replacing it, because a
            // widget added while the PATCH was running has no entry in the
            // fallback and would disappear.
            const live = liveLayoutRef.current;
            dispatch(
              updateWidgetPositioning({
                dashboardId: pending.dashboardId,
                positioning:
                  live.dashboardId === pending.dashboardId
                    ? { ...live.positioning, ...pending.previous }
                    : pending.previous,
              }),
            );
          }
        }
        return flushPositioning();
      })
      .catch(() => {
        // Safety net only: the PATCH use case reports failures instead of
        // throwing. If it ever did throw, the marker would stay set and block
        // every later save.
        persistInFlightRef.current = null;
        return flushPositioning();
      });
    persistInFlightRef.current = send;
    return send;
  }, [dispatch]);

  handleGridChangeRef.current = useCallback(
    (items: GridStackNode[]) => {
      // Read-only viewer: ignore ALL change events so a stray drag
      // (e.g. on a touch device or while staticGrid takes effect
      // lazily) cannot patch widget positions to the BE.
      if (readOnly) return;
      if (isUpdatingFromRedux.current || !selectedDashboardId) return;

      // Key every entry by the widget's dashed id (backend may return undashed
      // keys) so the PATCH payload never carries both forms for the same widget.
      const movedById = new Map(
        items
          .filter((item): item is GridStackNode & { id: string } => !!item.id)
          .map((item) => [item.id, item]),
      );
      const newPositioning: Record<string, WidgetPositioning> = {};
      Object.keys(widgets).forEach((widgetId) => {
        const moved = movedById.get(widgetId);
        if (moved) {
          newPositioning[widgetId] = {
            x: moved.x ?? 0,
            y: moved.y ?? 0,
            w: moved.w ?? 1,
            h: moved.h ?? 1,
          };
        } else {
          const pos =
            positioning[widgetId] ?? positioning[widgetId.replace(/-/g, "")];
          if (pos) newPositioning[widgetId] = pos;
        }
      });

      dispatch(
        updateWidgetPositioning({
          dashboardId: selectedDashboardId,
          positioning: newPositioning,
        }),
      );

      // A drag emits several change events, and only the first one carries the
      // layout to fall back to, so we capture that layout once per PATCH.
      persistPositioningRef.current = {
        dashboardId: selectedDashboardId,
        positioning: newPositioning,
        previous: persistPositioningRef.current?.previous ?? positioning,
      };
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      persistTimerRef.current = setTimeout(flushPositioning, 400);
    },
    [
      dispatch,
      selectedDashboardId,
      positioning,
      widgets,
      flushPositioning,
      readOnly,
    ],
  );

  // Flush any pending persist on unmount or when switching dashboards, so the
  // final drag position is never silently discarded (nor sent to the wrong
  // dashboard).
  useEffect(() => {
    // Fire-and-forget on unmount/switch: ordering is only awaited in the
    // delete/duplicate handlers, not here.
    return () => {
      flushPositioning();
    };
  }, [flushPositioning, dashboard.id]);

  // Initialize GridStack once per dashboard
  useEffect(() => {
    if (!gridRef.current) return;

    const columnCount = dashboard.columnCount || DEFAULT_GRID_COLUMNS;

    // Make existing grid-stack-item children known to GridStack
    const grid = GridStack.init(
      {
        column: columnCount,
        cellHeight: CELL_HEIGHT,
        // No gaps between widgets (uniform); the tile border provides separation.
        margin: 0,
        animate: true,
        float: true,
        removable: false,
        minRow: 1,
        // Read-only viewer page: lock down the grid so tiles can't be
        // dragged or resized by mouse/touch. Combined with the
        // no-op change handler below, this is a belt-and-braces
        // guarantee that no user interaction on `/dashboard` can
        // persist new positions to the BE.
        staticGrid: readOnly,
      },
      gridRef.current,
    );

    gridInstanceRef.current = grid;
    // After init, Gridstack is in charge of every .grid-stack-item child in
    // `gridRef.current`. Cache each one's element by widget id so the sync
    // effect below can route removeWidget calls (and makeWidget for newly
    // added tiles) without re-querying the DOM, which returns null after
    // React unmounts a deleted tile.
    Object.keys(widgets).forEach((id) => {
      const el = gridRef.current?.querySelector(
        `[gs-id="${id}"]`,
      ) as HTMLElement | null;
      if (el) managedTilesRef.current.set(id, el);
    });

    grid.on("change", (_event: Event, changedItems: GridStackNode[]) => {
      handleGridChangeRef.current(changedItems);
    });

    return () => {
      grid.off("change");
      grid.destroy(false);
      gridInstanceRef.current = null;
      managedTilesRef.current.clear();
    };
    // Init ONLY on dashboard identity / column-count changes. Adding/removing
    // widgets is handled by the sync effect below — re-init here would wipe
    // existing tiles' min/max constraints without restoring them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboard.id, dashboard.columnCount, readOnly]);

  // Incremental add/remove: surface new tiles via makeWidget (constraints
  // passed in opts so they're not dependent on DOM attr re-reads) and drop
  // deleted tiles via removeWidget. We hold element refs in
  // `managedTilesRef` across React unmounts so removed tiles can still reach
  // their gridstack engine node for proper teardown.
  useEffect(() => {
    const grid = gridInstanceRef.current;
    if (!grid) return;
    const currentIds = Object.keys(widgets);
    const managed = managedTilesRef.current;

    currentIds.forEach((id) => {
      const pos = positioning[id] ?? positioning[id.replace(/-/g, "")];
      if (!pos) return; // not yet rendered by React — will sync on next render
      const el = gridRef.current?.querySelector(
        `[gs-id="${id}"]`,
      ) as GridItemHTMLElement | null;
      if (!el) return;
      const widget = widgets[id];
      // `pos.h` is passed for every type; only content-sized types (see
      // widget-sizes) read it, so no other widget's constraints change.
      const size = getWidgetSize(widget.type, pos.h);
      if (managed.has(id)) {
        // GridStack reads a widget's constraints only when the widget is
        // created, so a position or size written outside a drag must be pushed
        // through `update`.
        const node = el.gridstackNode;
        if (
          node &&
          (node.x !== pos.x ||
            node.y !== pos.y ||
            node.w !== pos.w ||
            node.h !== pos.h)
        ) {
          // `update` fires a `change` event of its own, which the handler would
          // read as a user drag and turn into an unnecessary PATCH. try/finally
          // keeps a throw from leaving the flag set and stopping later saves.
          isUpdatingFromRedux.current = true;
          try {
            grid.update(el, {
              x: pos.x,
              y: pos.y,
              w: pos.w,
              h: pos.h,
              minH: size.minH,
              maxH: size.maxH,
            });
          } finally {
            isUpdatingFromRedux.current = false;
          }
        }
        return;
      }
      grid.makeWidget(el, {
        id,
        x: pos.x,
        y: pos.y,
        w: pos.w,
        h: pos.h,
        minW: size.minW,
        minH: size.minH,
        maxW: size.maxW,
        maxH: size.maxH,
      });
      managed.set(id, el);
    });

    Array.from(managed.entries()).forEach(([id, el]) => {
      if (currentIds.includes(id)) return;
      grid.removeWidget(el, false); // removeDOM=false: React owns the DOM
      managed.delete(id);
    });
  }, [widgets, positioning]);

  // Click-outside handler for menus
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuWidgetId(null);
      }
    };

    if (openMenuWidgetId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuWidgetId]);

  const handleMenuToggle = (widgetId: string) => {
    if (openMenuWidgetId === widgetId) {
      setOpenMenuWidgetId(null);
      setPopoverAnchor(null);
      return;
    }
    const btn = buttonRefsRef.current.get(widgetId);
    const rect = btn?.getBoundingClientRect();
    if (rect) setPopoverAnchor({ rect, widgetId });
    setOpenMenuWidgetId(widgetId);
  };

  const handleEdit = (widgetId: string) => {
    dispatch(updateEditWidgetDrawerModalTileId(widgetId));
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: true,
        id: "editWidgetDrawerModal",
      }),
    );
    setOpenMenuWidgetId(null);
  };

  const handleDuplicate = async (widgetId: string) => {
    // Immediate server-side duplicate. Click → BE clone POST with the
    // `widget_positioning` picked from GridStack → reducer splice. No
    // draft entry, no edit drawer.
    // The chosen `widget_positioning` is what we forward to the BE; the BE may
    // still nudge it (e.g. doc §2.5.1 the copy goes below the original).
    setOpenMenuWidgetId(null);

    const original = dashboard.widgets[widgetId];
    const pos =
      positioning[widgetId] ?? positioning[widgetId.replace(/-/g, "")];
    if (!original || !pos) {
      return;
    }
    if (pendingWidgetIds.includes(widgetId)) {
      // The menu already gates pending ids off, but a stale closure
      // between render and click shouldn't be allowed to clone an
      // unsaved draft — the BE has never seen a pending id and would
      // 404. Re-check defensively.
      return;
    }
    const columns = dashboard.columnCount || DEFAULT_GRID_COLUMNS;
    const overlap = (a: WidgetPositioning, b: WidgetPositioning) =>
      a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
    // Occupied except for the widget we're cloning (it would otherwise
    // overlap with itself on a single-tile dashboard).
    const occupied: WidgetPositioning[] = Object.entries(positioning)
      .filter(([id]) => id !== widgetId && id !== widgetId.replace(/-/g, ""))
      .map(([, p]) => p);
    const candidates: WidgetPositioning[] = [
      { x: pos.x + pos.w, y: pos.y, w: pos.w, h: pos.h },
      { x: pos.x, y: pos.y + pos.h, w: pos.w, h: pos.h },
    ];
    const firstFree =
      candidates.find(
        (c) =>
          c.x + pos.w <= columns &&
          c.y + pos.h >= 0 &&
          !occupied.some((o) => overlap(o, c)),
      ) ??
      (() => {
        const slot = findFreeSlot(occupied, columns, pos.w, pos.h);
        return { x: slot.x, y: slot.y, w: pos.w, h: pos.h };
      })();
    const targetPos: WidgetPositioning = firstFree;

    const result = await postDuplicateWidgetV2UseCase(
      dashboard.id,
      widgetId,
      targetPos,
    );
    if (!result.success) {
      errorSnackbar(result.error ?? "Failed to duplicate widget");
      return;
    }
    if (result.widget) {
      dispatch(
        addServerCommittedWidget({
          dashboardId: dashboard.id,
          widget: result.widget,
          positioning: targetPos,
        }),
      );
    }
  };

  const handleDelete = async (widgetId: string) => {
    // Pending widget (pick-then-save / duplicate): purely local — the BE has
    // never seen its id, so `deleteWidgetV2UseCase(...)` would 404. Dispatch
    // the local cleanup and skip the round-trip entirely.
    if (pendingWidgetIds.includes(widgetId)) {
      if (!selectedDashboardId) {
        setOpenMenuWidgetId(null);
        return;
      }
      dispatch(
        removePendingWidget({
          dashboardId: selectedDashboardId,
          tempId: widgetId,
        }),
      );
      setOpenMenuWidgetId(null);
      return;
    }
    if (!selectedDashboardId) {
      setOpenMenuWidgetId(null);
      return;
    }
    // Flush any pending position PATCH *before* the delete, so a stale payload
    // can't re-add the deleted widget's positioning after the DELETE wins.
    await flushPositioning();
    const result = await deleteWidgetV2UseCase(selectedDashboardId, widgetId);
    if (result.success) {
      // Re-fetch the dashboard so state matches the BE truth (the delete also
      // removes the widget's positioning entry on the server).
      const refreshed = await getDashboardByIdV2UseCase(selectedDashboardId);
      if (refreshed.success && refreshed.data) {
        dispatch(upsertDashboard(refreshed.data));
      }
    } else {
      console.error("Failed to delete widget:", result.error);
      errorSnackbar(result.error ?? "Failed to delete widget");
    }
    setOpenMenuWidgetId(null);
  };

  return (
    <div ref={tileGridContainerRef} className={styles[cssClassName]}>
      <div ref={gridRef} className="grid-stack">
        {Object.keys(widgets).map((widgetId) => {
          const widget = widgets[widgetId];
          // Backend returns positioning keyed by the UUID without dashes, while
          // widgets are keyed with dashes — fall back to the stripped key.
          const pos =
            positioning[widgetId] ?? positioning[widgetId.replace(/-/g, "")];

          if (!pos) return null;

          const size = getWidgetSize(widget.type, pos.h);

          return (
            <div
              key={widgetId}
              className={`grid-stack-item ${styles[`${cssClassName}__tile`]}`}
              gs-id={widgetId}
              gs-x={String(pos.x)}
              gs-y={String(pos.y)}
              gs-w={String(pos.w)}
              gs-h={String(pos.h)}
              gs-min-w={String(size.minW)}
              gs-min-h={String(size.minH)}
              gs-max-w={String(size.maxW)}
              gs-max-h={size.maxH === undefined ? undefined : String(size.maxH)}
            >
              <div
                className={cn(
                  "grid-stack-item-content",
                  styles[`${cssClassName}__tile-inner`],
                  {
                    [styles[`${cssClassName}__tile-inner--menu-open`]]:
                      openMenuWidgetId === widgetId,
                    [styles[`${cssClassName}__tile-inner--transparent`]]:
                      widget.presentation?.transparent === true,
                  },
                )}
              >
                {!readOnly && (
                  <div
                    className={styles[`${cssClassName}__tile-menu`]}
                    ref={openMenuWidgetId === widgetId ? menuRef : undefined}
                  >
                    <button
                      ref={(el) => {
                        if (el) buttonRefsRef.current.set(widgetId, el);
                        else buttonRefsRef.current.delete(widgetId);
                      }}
                      className={styles[`${cssClassName}__tile-menu-button`]}
                      onClick={() => handleMenuToggle(widgetId)}
                      aria-label="Widget actions"
                    >
                      <img src={ellipsisIcon} alt="" />
                    </button>
                  </div>
                )}
                <div className={styles[`${cssClassName}__tile-content`]}>
                  <TrackVisibility scrollContainerRef={tileGridContainerRef}>
                    {(inView) => (
                      <WidgetRenderer
                        type={widget.type}
                        title={widget.title}
                        encoding={widget.encoding}
                        semantics={widget.semantics}
                        presentation={widget.presentation}
                        image_url={widget.image_url ?? null}
                        query={widget.query}
                        showTitle
                        inViewport={inView}
                      />
                    )}
                  </TrackVisibility>
                </div>
              </div>
            </div>
          );
        })}

        {/* Tile menu popover — portaled to document.body so it floats above
            the canvas and is not clipped by the tile-inner's
            `overflow: hidden` nor pushed into the canvas's
            `overflow-y: auto` scrollbar. Position computed from the menu
            button's viewport rect (captured at click time). */}
        {openMenuWidgetId &&
          popoverAnchor &&
          createPortal(
            <div
              ref={menuRef}
              className={styles[`${cssClassName}__tile-menu-popover`]}
              style={{
                position: "fixed",
                top: popoverAnchor.rect.bottom + 4,
                left: popoverAnchor.rect.right - 120,
                right: "auto",
              }}
            >
              <button
                className={styles[`${cssClassName}__tile-menu-item`]}
                onClick={() => {
                  if (openMenuWidgetId) handleEdit(openMenuWidgetId);
                }}
              >
                {texts.editWidget}
              </button>
              {openMenuWidgetId &&
                !pendingWidgetIds.includes(popoverAnchor.widgetId) && (
                  <button
                    className={styles[`${cssClassName}__tile-menu-item`]}
                    onClick={() => {
                      if (openMenuWidgetId) handleDuplicate(openMenuWidgetId);
                    }}
                  >
                    {texts.duplicateWidget}
                  </button>
                )}
              <button
                className={cn(
                  styles[`${cssClassName}__tile-menu-item`],
                  styles[`${cssClassName}__tile-menu-item--delete`],
                )}
                onClick={() => {
                  if (openMenuWidgetId) handleDelete(openMenuWidgetId);
                }}
              >
                {texts.deleteWidget}
              </button>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};
